// Finishing a FreeAgent/Xero connection. cover-connect sends the browser back
// to the dashboard (or ops console) with ?pending={provider}&ref=… and holds
// the connection until the same customer (or staff member) confirms it. The
// page posts the ref to /connect/{provider}/complete on its own Worker, which
// adds the caller's credential and forwards it to cover-connect. Only after a
// 200 does the connection exist and start syncing.
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/Button.tsx';

export type ConnectProvider = 'freeagent' | 'xero';

const NAME: Record<ConnectProvider, string> = { freeagent: 'FreeAgent', xero: 'Xero' };

export type ConnectState =
  | { phase: 'idle' }
  | { phase: 'connecting'; provider: ConnectProvider }
  | { phase: 'connected'; provider: ConnectProvider; accountName: string | null }
  /** `retry`: start again, or sign in as the right person (the ref is still usable). */
  | { phase: 'failed'; provider: ConnectProvider | null; code: string; message: string; retry: 'again' | 'signin' | 'none' };

function isProvider(p: string | null): p is ConnectProvider {
  return p === 'freeagent' || p === 'xero';
}

/** Plain-English copy for cover-connect's error codes (redirect `?error=` or the complete call). */
export function connectErrorMessage(code: string, provider: ConnectProvider | null, who: 'customer' | 'staff'): string {
  const name = provider ? NAME[provider] : 'your accounts';
  switch (code) {
    case 'link_used':
      return 'That connect link was already used. Start again from here.';
    case 'different_browser':
      return `You started connecting ${name} in a different browser. Start again here, in this one.`;
    case 'company_mismatch':
      return `Those ${name} books are for a different company, so nothing was connected.`;
    case 'not_approved':
    case 'access_denied':
      return `${name} wasn’t approved, so nothing was shared.`;
    case 'choose_one_organisation':
      return `Choose one organisation in ${name}, then connect again.`;
    case 'expired':
      return 'That connection timed out or was already confirmed. Connect again to finish.';
    case 'forbidden':
      return who === 'customer'
        ? 'You’re signed in as someone else. Sign in as the person who started connecting, then come back to this page to finish.'
        : 'Only the staff member who started this connection can confirm it. Ask them to open this page while signed in.';
    case 'unauthorised':
      return 'Please sign in again to finish connecting.';
    case 'not_configured':
      return `Connecting ${name} isn’t switched on yet. Nothing was shared; we’ll let you know when it’s ready.`;
    default:
      return `${name} didn’t connect (${code.replace(/_/g, ' ')}). Please try again.`;
  }
}

// One attempt per ref, even if React mounts the page twice.
const attempted = new Set<string>();

/**
 * Handle the return from cover-connect on the current page. `params` are the
 * page's search params; `clear(keys)` removes them from the URL once handled.
 */
export function useConnectReturn({ params, clear, who, headers, onConnected }: { params: URLSearchParams; clear: (keys: string[]) => void; who: 'customer' | 'staff'; headers?: () => Record<string, string>; onConnected?: () => void }) {
  const [state, setState] = useState<ConnectState>({ phase: 'idle' });
  const latest = useRef({ clear, headers, onConnected });
  latest.current = { clear, headers, onConnected };

  const pending = params.get('pending');
  const ref = params.get('ref');
  const error = params.get('error');
  const connected = params.get('connected');
  const providerParam = params.get('provider');

  useEffect(() => {
    const { clear, headers, onConnected } = latest.current;
    if (error) {
      const provider = isProvider(pending) ? pending : isProvider(providerParam) ? providerParam : null;
      setState({ phase: 'failed', provider, code: error, message: connectErrorMessage(error, provider, who), retry: error === 'not_configured' ? 'none' : 'again' });
      clear(['error', 'pending', 'ref', 'provider']);
      return;
    }
    if (isProvider(connected)) {
      setState({ phase: 'connected', provider: connected, accountName: null });
      clear(['connected']);
      onConnected?.();
      return;
    }
    if (!isProvider(pending) || !ref || attempted.has(ref)) return;
    attempted.add(ref);
    setState({ phase: 'connecting', provider: pending });
    (async () => {
      const res = await fetch(`/connect/${pending}/complete`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...(headers?.() ?? {}) },
        body: JSON.stringify({ ref }),
      }).catch(() => null);
      const body = (await res?.json().catch(() => ({}))) as { externalAccountName?: string | null; error?: { code: string; message: string } } | undefined;
      if (res?.ok) {
        setState({ phase: 'connected', provider: pending, accountName: body?.externalAccountName ?? null });
        clear(['pending', 'ref']);
        onConnected?.();
        return;
      }
      const code = body?.error?.code ?? (res ? `http_${res.status}` : 'network');
      const signin = res?.status === 403 || res?.status === 401;
      const unavailable = code === 'not_configured';
      // A wrong-person 403 leaves the ref usable, so keep it in the URL for after sign-in.
      if (signin) attempted.delete(ref);
      else clear(['pending', 'ref']);
      setState({ phase: 'failed', provider: pending, code, message: connectErrorMessage(res?.status === 410 ? 'expired' : res?.status === 403 ? 'forbidden' : res?.status === 401 ? 'unauthorised' : code, pending, who), retry: signin ? 'signin' : unavailable ? 'none' : 'again' });
    })();
  }, [pending, ref, error, connected, providerParam, who]);

  return { state, dismiss: () => setState({ phase: 'idle' }) };
}

/** Status for the return from cover-connect: "Connecting…", connected, or what went wrong. */
export function ConnectBanner({ state, onConnectAgain, onSignIn, onDismiss }: { state: ConnectState; onConnectAgain?: (p: ConnectProvider) => void; onSignIn?: () => void; onDismiss: () => void }) {
  if (state.phase === 'idle') return null;
  if (state.phase === 'connecting')
    return (
      <div role="status" className="flex items-center gap-3 rounded-lg bg-info-bg p-4 text-info-fg">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
        <p className="font-semibold">Connecting {NAME[state.provider]}…</p>
      </div>
    );
  if (state.phase === 'connected')
    return (
      <div role="status" className="space-y-2 rounded-lg bg-ok-bg p-4 text-ok-fg sm:flex sm:items-center sm:gap-3 sm:space-y-0">
        <div className="flex gap-3 sm:flex-1">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <p>
            <strong>
              {NAME[state.provider]} connected{state.accountName ? `: ${state.accountName}` : ''}.
            </strong>{' '}
            We’ll pull in what we can, and nothing is used until it’s been checked.
          </p>
        </div>
        <Button size="sm" variant="ghost" className="ml-6 sm:ml-0" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    );
  return (
    <div role="alert" className="space-y-3 rounded-lg bg-warn-bg p-4 text-warn-fg sm:flex sm:items-center sm:gap-3 sm:space-y-0">
      <div className="flex gap-3 sm:flex-1">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <p className="min-w-0">{state.message}</p>
      </div>
      <div className="ml-8 flex flex-wrap gap-2 sm:ml-0 sm:shrink-0">
        {state.retry === 'signin' && onSignIn && (
          <Button size="sm" onClick={onSignIn}>
            Sign in as someone else
          </Button>
        )}
        {state.retry === 'again' && state.provider && onConnectAgain && (
          <Button size="sm" onClick={() => onConnectAgain(state.provider!)}>
            Connect again
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}
